import useCurrencyConversion from '@/src/utils/useCurrencyConversion';
import useFormatterPrice from '@/src/utils/formatter-price';
import { useLazyCalculateShippingQuery } from '@api/api';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import getOption from '@/src/utils/getOption';
import '@/src/components/Accordion.css';
import { MdArrowForwardIos } from 'react-icons/md';
import { useState } from 'react';
import useIfDefaultCurrency from '@/src/utils/useIfDefaultCurrency';

const ports = {
  no: {
    '3004': 'Drammen',
    '5003': 'Bergen',
    '6002': 'Ålesund',
    '9006': 'Tromsø',
    '9510': 'Alta',
  },
  sv: {
    '94143': 'Piteå',
    '97437': 'Luleå',
    '93233': 'Skellefteå',
    '91322': 'Umeå, Holmsund',
    '85121': 'Sundsvall',
    '82450': 'Hudiksvall',
    '80647': 'Gävle',
    '15138': 'Södertälje',
    '76015': 'Kapellskär (Stockholm)',
    '14930': 'Nynäshamn',
    '10254': 'Stockholm',
    '60238': 'Norrköping',
    '37493': 'Karlshamn',
    '27139': 'Ystad',
    '20125': 'Malmö',
    '25189': 'Helsingborg',
    '30250': 'Halmstad',
    '41463': 'Göteborg',
    '47199': 'Wallhamn',
    '45150': 'Uddevalla',
  },
};

export default function ShippingCalculator({
  adId,
  shippingType,
}: {
  adId: number;
  shippingType: string;
}) {
  const t = useTranslations('ShippingCalculator');
  const isDefaultCurrency = useIfDefaultCurrency();
  const convert = useCurrencyConversion();
  const formatterPrice = useFormatterPrice();
  const defaultLocale = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;
  const [active, setActive] = useState(false);
  const [type, setType] = useState('postcode');

  const handleToggle = () => {
    setActive(!active);
  };

  const [queryCalculate, { data, isFetching, isError }] =
    useLazyCalculateShippingQuery();
  const { register, handleSubmit, setValue, getValues } = useForm();
  const calculate = (formData: any) => {
    queryCalculate(formData);
  };
  return (
    <form
      onSubmit={handleSubmit(calculate)}
      className='flex flex-col rounded bg-brand-dark p-4 text-dark'
    >
      <button
        onClick={() => handleToggle()}
        aria-expanded={active}
        className={`accordion-trigger block w-full pr-10 text-left lg:cursor-default`}
      >
        <h3 className='font-bold'>{t('title')}</h3>
        <span className='accordion-state lg:!hidden'>
          <MdArrowForwardIos />
        </span>
      </button>

      <div className='accordion-content lg:!grid-rows-1' aria-hidden={!active}>
        <div className='flex flex-col gap-3'>
          <input type='hidden' {...register('ad_id')} value={adId} />
          <input
            type='hidden'
            {...register('shipping_type')}
            value={shippingType}
          />
          <input
            type='hidden'
            {...register('country')}
            value={getOption('DEFAULT_LOCALE') === 'no' ? 'Norway' : 'Sweden'}
          />
          <div className='-mb-3 mt-2 flex items-end'>
            <button
              onClick={() => setType('postcode')}
              type='button'
              className={`w-1/2 border-l-2 border-t-2 border-black p-1 font-semibold ${type === 'postcode' ? 'bg-black py-2 text-white' : 'bg-brand text-black'}`}
            >
              {t('label.postcode')}
            </button>
            <button
              onClick={() => setType('port')}
              type='button'
              className={`w-1/2 border-r-2 border-t-2 border-black p-1 font-semibold ${type === 'port' ? 'bg-black py-2 text-white' : 'bg-brand text-black'}`}
            >
              {t('label.port')}
            </button>
          </div>
          {type === 'postcode' ? (
            <input
              className='form-input w-full rounded-sm p-2 text-dark '
              placeholder={t('placeholder.zip')}
              {...register('zipcode', {
                required: {
                  value: true,
                  message: t('validation.zipRequired'),
                },
              })}
            />
          ) : (
            <select
              onChange={(e) => setValue('zipcode', e.target.value)}
              className='form-select w-full rounded-sm p-2 py-3 text-dark'
            >
              {Object.entries(
                ports[getOption('DEFAULT_LOCALE') as keyof typeof ports]
              ).map(([key, value]) => {
                return (
                  <option
                    key={key}
                    value={key}
                    selected={getValues('zipcode') === key}
                  >
                    {value}
                  </option>
                );
              })}
            </select>
          )}
          <button
            type='submit'
            className='grow rounded-sm bg-dark p-2 font-semibold uppercase text-white transition-all hover:bg-brand-alt focus:outline-none'
          >
            {t('label.submit')}
          </button>
          {isFetching && <>{t('calculating')}...</>}
          {data && !isError && (
            <span className='font-bold'>
              {t('total')}: {formatterPrice(data.data.total, defaultLocale)}
              {!isDefaultCurrency && (
                <span className='text-small ml-2'>
                  {'('}
                  {t('approx')}. {formatterPrice(convert(data.data.total))}
                  {')'}
                </span>
              )}
            </span>
          )}
          {isError && <span>{t('isError')}</span>}
        </div>
      </div>
    </form>
  );
}
